import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, tap } from 'rxjs/operators';
import {
    DatePrecision,
    DISPLAY_DATE_FORMATS,
    READONLY_DATE_FORMATS,
    popperDateTimeOptions,
    MaskSettings,
} from '@bazis/form/models/form-element.types';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DateValidators } from '@bazis/form/validators/dateValidators';
import { Observable } from 'rxjs';
import { createPopper, Instance, Modifier, OptionsGeneric } from '@popperjs/core';
import moment from 'moment';
import { COMMON_DATE_MASK_SETTINGS } from '@bazis/configuration.service';
import { BazisControlValueAccessor } from '@bazis/form/components/control-value-accessor.component';

@UntilDestroy()
@Component({
    selector: 'bazis-input-date',
    templateUrl: './input-date.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class BazisInputDateComponent
    extends BazisControlValueAccessor
    implements AfterViewInit, OnChanges, OnDestroy
{
    @ViewChild('calendar') calendar: ElementRef;

    @ViewChild('inputDate') inputDate: ElementRef;

    @Input() mode: 'picker' | 'input' | 'mixed' = 'picker';

    @Input() minDate: string = null;

    @Input() maxDate: string = null;

    @Input() inputFormat: string = 'YYYY-MM-DD';

    @Input() precision: DatePrecision = 'days';

    @Input() canToggleDay: boolean = true;

    // for datepicker (minYear)
    @Input() startYear: number = null;

    // for datepicker (list with All years)
    @Input() visibleAllYears: boolean = false;

    // маркеры для календаря
    @Input() marks$: Observable<{ [index: number]: string }>;

    // настройки popper
    @Input() popperOptions: Partial<OptionsGeneric<Partial<Modifier<any, any>>>> = null;

    @Input()
    disabledIntervals: string[][] = null;

    @Input() placeIcon: 'left' | 'right' = 'right';

    datapickerViewConfig;

    disabled = false;

    dateValue = new FormControl();

    calendarValue: Date[] = [null];

    displayFormat: string;

    readonlyFormat: string;

    maskSettings: MaskSettings;

    showCalendar = false;

    mask = '';

    instanceDatePopper: Instance;

    isClickOnPopper = false;

    hasCleared = false;

    extendOnInit() {
        this.datapickerViewConfig = this.configurationService.datapickerViewConfig;

        this.initFormats();

        this.initValidators();

        this.dateValue.valueChanges
            .pipe(
                debounceTime(0),
                tap(() => {
                    this.setControlValue();
                    this.setCalendarValue();
                }),
                untilDestroyed(this),
            )
            .subscribe();

        this.dateValue.statusChanges
            .pipe(
                debounceTime(0),
                tap((status) => {
                    if (status !== 'INVALID') {
                        let errors = this.ngControl.control.errors;
                        if (!errors) return;
                        delete errors.componentError;
                        errors = Object.keys(errors).length > 0 ? errors : null;
                        this.ngControl.control.setErrors(errors);
                    } else {
                        this.ngControl.control.setErrors({
                            ...this.ngControl.control.errors,
                            componentError: { ...this.dateValue.errors },
                        });
                    }
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }

    initFormats() {
        this.displayFormat = DISPLAY_DATE_FORMATS[this.precision];
        this.readonlyFormat = READONLY_DATE_FORMATS[this.precision];

        this.maskSettings = {
            ...COMMON_DATE_MASK_SETTINGS,
            mask: this.displayFormat.replace(/[A-Za-z]/g, '0'),
        };

        if (!this.placeholderKey && this.mode === 'input') {
            this.placeholderKey = 'form.placeholder.date.' + this.precision;
        }
    }

    ngAfterViewInit() {
        setTimeout(() => {
            if (!this.inputDate || !this.calendar) return;
            const reference = this.inputDate.nativeElement;
            const popper = this.calendar.nativeElement;

            this.instanceDatePopper = createPopper(reference, popper, {
                ...popperDateTimeOptions,
                ...this.popperOptions,
            });
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        // console.log('[DEBUG] changes', changes, this.controlDate['$config']['name']);

        if (changes.minDate || changes.maxDate) {
            this.initValidators();
            this.dateValue.updateValueAndValidity();
        }
    }

    ngOnDestroy() {
        if (this.instanceDatePopper) this.instanceDatePopper.destroy();
    }

    initValidators() {
        const validators = [];
        if (this.minDate) {
            validators.push(
                DateValidators.minDateValidatorFn(this.displayFormat, this.precision, this.minDate),
            );
        }
        if (this.maxDate) {
            validators.push(
                DateValidators.maxDateValidatorFn(this.displayFormat, this.precision, this.maxDate),
            );
        }
        validators.push(DateValidators.invalidDateValidatorFn(this.displayFormat));
        this.dateValue.setValidators(validators);
    }

    public setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.dateValue.disable();
        } else {
            this.dateValue.enable();
        }
    }

    public writeValue(value): void {
        if (!this.ngControl.control) return;
        if (!value) {
            this.dateValue.setValue(null);
            this.setCalendarValue();
            return;
        }
        const controlValue = moment(value, this.inputFormat);
        if (!controlValue.isValid()) return;
        const displayValue = moment(controlValue, this.inputFormat).format(this.displayFormat);
        this.dateValue.setValue(displayValue);
        this.setCalendarValue();
    }

    setValueFromCalendar(value: string[]) {
        this.dateValue.setValue(value[0]);
        this.showCalendar = false;
        this.hasCleared = false;
    }

    getDateValueMoment() {
        return moment(this.dateValue.value, this.displayFormat, true);
    }

    setCalendarValue() {
        const date = this.getDateValueMoment();
        this.calendarValue = date.isValid() ? [date.toDate()] : [null];
    }

    setControlValue() {
        const date = this.getDateValueMoment();
        let dateValue = date.isValid() ? date.format(this.inputFormat) : null;
        const value = this.dateValue.value ? dateValue : null;
        if (value !== this.ngControl.control.value) {
            this.onChange(value);
        }
    }

    openCalendar(e) {
        this.showCalendar = !this.showCalendar;
        if (this.instanceDatePopper) this.instanceDatePopper.update();
    }

    clickPopper() {
        this.isClickOnPopper = true;
    }

    clickPopperOutside() {
        this.showCalendar = false;
        this.isClickOnPopper = false;
    }

    onClear() {
        this.ngControl.control.setValue('');
        this.hasCleared = true;
    }
}
